/**
 * AdminConsultationsTab — inbox for consultation-request leads.
 *
 * Wave 5-Late Marketplace propagation. Executive admin discipline
 * inherited from Marketplace anchor (AdminProfessionalsTab):
 *   • Section primitive with hairline `--ds-color-admin-border` chrome.
 *   • Filter Section body `p-3`, section spacing `mb-3`.
 *   • List Section body `p-0`, `overflow-x-auto` wrapper on the grid.
 *   • Row density `py-1.5`, `border-bottom` with last-item guard.
 *   • Status pill via DS Badge with muted admin tones + `shape="sharp"`.
 *   • Inputs on `--ds-radius-input` (3px) + `--ds-color-input-border`.
 *   • tabular-nums on numeric spans.
 *
 * Business logic, API surface, testids, and the "forward → close with
 * reason" workflow preserved BYTE-EXACT.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Copy, Inbox, RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/api/client";
import { Heading } from "@/design-system/components/Heading";
import { Badge } from "@/design-system/components/Badge";
import { AccentButton, GhostButton, Section, SelectInput, TextInput } from "@/admin/_primitives";

const STATUS_FILTERS = ["all", "pending", "forwarded", "closed"];
const CLOSED_REASONS = [
  { value: "user_cancelled", label: "User cancelled" },
  { value: "professional_unreachable", label: "Professional unreachable" },
  { value: "consultation_completed", label: "Consultation completed" },
  { value: "other", label: "Other" },
];

// Executive Badge tone mapping. `forwarded` uses `info` (blue-soft is
// acceptable here because it's a transient state signalling in-flight,
// not the "primary success" state — matches Ramp inbox convention).
const STATUS_TONE = {
  pending: "warning",
  forwarded: "info",
  closed: "neutralMuted",
};

function formatDate(s) {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return s;
  }
}

async function copyToClipboard(text) {
  if (!text) return;
  try {
    await navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard.");
  } catch {
    toast.error("Copy failed. Highlight manually.");
  }
}

/* ------------------------------------------------------------------ */
/* Local admin primitives (mirror Marketplace anchor).                  */
/* ------------------------------------------------------------------ */


function Field({ label, children }) {
  return (
    <label className="block">
      <span
        className="block text-[10.5px] uppercase tracking-[0.16em] font-semibold mb-1"
        style={{ color: "var(--ds-color-ink-subtle)" }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}





const GRID_COLS = "grid-cols-[130px_1fr_1fr_1fr_90px]";

/* ------------------------------------------------------------------ */
/* Component                                                            */
/* ------------------------------------------------------------------ */

export default function AdminConsultationsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState("all");
  const [proFilter, setProFilter] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [professionals, setProfessionals] = useState({});
  const [closingId, setClosingId] = useState(null);
  const [closeReason, setCloseReason] = useState("consultation_completed");

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: 200 };
      if (status !== "all") params.status = status;
      const { data } = await api.get(
        "/admin/marketplace/consultation-requests",
        { params },
      );
      setRows(Array.isArray(data) ? data : []);
      const proRes = await api.get("/admin/marketplace/professionals", {
        params: { limit: 500 },
      });
      const map = {};
      for (const p of proRes.data || []) map[p.id] = p;
      setProfessionals(map);
    } catch (err) {
      setError(formatApiError(err));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const filtered = useMemo(() => {
    let out = rows;
    if (proFilter) out = out.filter((r) => r.professional_id === proFilter);
    if (userFilter.trim()) {
      const needle = userFilter.trim().toLowerCase();
      out = out.filter(
        (r) =>
          (r.user_display_name || "").toLowerCase().includes(needle) ||
          (r.user_contact_email || "").toLowerCase().includes(needle),
      );
    }
    return out;
  }, [rows, proFilter, userFilter]);

  const proOptions = useMemo(
    () => Object.values(professionals).sort((a, b) => a.name.localeCompare(b.name)),
    [professionals],
  );

  const selected = useMemo(
    () => filtered.find((r) => r.id === selectedId) || null,
    [filtered, selectedId],
  );

  async function forwardLead(id) {
    try {
      const { data } = await api.patch(
        `/admin/marketplace/consultation-requests/${id}`,
        { action: "forward" },
      );
      setRows((rs) => rs.map((r) => (r.id === id ? data : r)));
      toast.success("Marked forwarded.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  async function closeLead(id, reason) {
    if (!reason) {
      toast.error("Pick a close reason first.");
      return;
    }
    setClosingId(id);
    try {
      const { data } = await api.patch(
        `/admin/marketplace/consultation-requests/${id}`,
        { action: "close", closed_reason: reason },
      );
      setRows((rs) => rs.map((r) => (r.id === id ? data : r)));
      toast.success("Consultation closed.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setClosingId(null);
    }
  }

  const filterBadge = (
    <GhostButton onClick={fetchRows} data-testid="admin-consultation-refresh" className="!py-1 !text-[11px]">
      <RefreshCw className="w-3 h-3" /> Refresh
    </GhostButton>
  );

  return (
    <div>
      <Section title="Filter consultations" icon={Search} badge={filterBadge} testid="admin-consultation-filters">
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Status">
            <SelectInput
              data-testid="admin-consultation-status-filter"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              {STATUS_FILTERS.map((s) => (
                <option key={s} value={s}>{s === "all" ? "All statuses" : s}</option>
              ))}
            </SelectInput>
          </Field>
          <Field label="Professional">
            <SelectInput
              data-testid="admin-consultation-pro-filter"
              value={proFilter}
              onChange={(e) => setProFilter(e.target.value)}
            >
              <option value="">All professionals</option>
              {proOptions.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </SelectInput>
          </Field>
          <div className="flex-1 min-w-[220px]">
            <Field label="User search">
              <TextInput
                data-testid="admin-consultation-user-filter"
                value={userFilter}
                onChange={(e) => setUserFilter(e.target.value)}
                placeholder="Filter by user name or email"
              />
            </Field>
          </div>
        </div>
      </Section>

      {error && (
        <div
          className="mb-3 rounded-[var(--ds-radius-field)] border px-3 py-2 text-[12px]"
          style={{
            borderColor: "var(--ds-color-danger)",
            color: "var(--ds-color-danger)",
            background: "var(--ds-color-danger-soft)",
          }}
        >
          {error}
        </div>
      )}

      <div className="grid gap-3 lg:grid-cols-[1fr_360px]">
        <Section
          title="Consultation leads"
          icon={Inbox}
          badge={
            <span
              className="text-[11px] uppercase tracking-[0.14em] tabular-nums"
              style={{ color: "var(--ds-color-ink-muted)" }}
            >
              {filtered.length} showing · {rows.length} total
            </span>
          }
          testid="admin-consultation-list-section"
          bodyClassName="p-0"
        >
          <div className="overflow-x-auto" data-testid="admin-consultation-list">
            <div
              className={`grid ${GRID_COLS} gap-3 px-4 py-2 text-[10px] uppercase tracking-[0.16em] font-semibold`}
              style={{
                color: "var(--ds-color-ink-subtle)",
                background: "var(--ds-color-admin-surface-muted)",
                borderBottom: "1px solid var(--ds-color-admin-border)",
              }}
            >
              <span>Created</span>
              <span>User</span>
              <span>Professional</span>
              <span>Topic</span>
              <span>Status</span>
            </div>
            {loading ? (
              <div
                className="px-4 py-8 text-[13px] text-center"
                style={{ color: "var(--ds-color-ink-muted)" }}
                data-testid="admin-consultation-loading"
              >
                Loading…
              </div>
            ) : filtered.length === 0 ? (
              <div
                className="px-4 py-10 text-[13px] text-center"
                style={{ color: "var(--ds-color-ink-muted)" }}
                data-testid="admin-consultation-empty"
              >
                No consultation requests match those filters.
              </div>
            ) : (
              filtered.map((r, idx) => {
                const pro = professionals[r.professional_id];
                const isLast = idx === filtered.length - 1;
                const isSelected = selectedId === r.id;
                return (
                  <button
                    key={r.id}
                    type="button"
                    data-testid="admin-consultation-list-row"
                    data-consultation-id={r.id}
                    data-status={r.status}
                    onClick={() => setSelectedId(r.id)}
                    className={`w-full text-left grid ${GRID_COLS} gap-3 px-4 py-1.5 text-[13px] items-center transition-colors`}
                    style={{
                      borderBottom: isLast
                        ? "none"
                        : "1px solid var(--ds-color-admin-border)",
                      background: isSelected
                        ? "var(--ds-color-accent-soft)"
                        : "transparent",
                      transitionDuration: "var(--ds-duration-fast)",
                    }}
                  >
                    <span className="text-[12px] tabular-nums" style={{ color: "var(--ds-color-ink-muted)" }}>
                      {formatDate(r.created_at)}
                    </span>
                    <span className="truncate" style={{ color: "var(--ds-color-ink)" }}>
                      {r.user_display_name || "—"}
                    </span>
                    <span className="truncate" style={{ color: "var(--ds-color-ink)" }}>
                      {pro?.name || r.professional_id}
                    </span>
                    <span className="truncate" style={{ color: "var(--ds-color-ink-muted)" }}>
                      {r.topic}
                    </span>
                    <Badge
                      tone={STATUS_TONE[r.status] || "neutralMuted"}
                      size="sm"
                      shape="sharp"
                      className="uppercase tracking-[0.14em] w-fit"
                    >
                      {r.status}
                    </Badge>
                  </button>
                );
              })
            )}
          </div>
        </Section>

        <Section title="Details" testid="admin-consultation-detail-section" bodyClassName="p-4">
          <div data-testid="admin-consultation-detail" className="space-y-3">
            {!selected ? (
              <div className="text-[13px]" style={{ color: "var(--ds-color-ink-muted)" }}>
                Select a row to see full request details.
              </div>
            ) : (
              (() => {
                const pro = professionals[selected.professional_id];
                return (
                  <>
                    <div className="flex items-center justify-between gap-2">
                      <Heading level={4} size="sm" weight="semibold" className="min-w-0 truncate">
                        {selected.topic}
                      </Heading>
                      <Badge
                        tone={STATUS_TONE[selected.status] || "neutralMuted"}
                        size="sm"
                        shape="sharp"
                        className="uppercase tracking-[0.14em]"
                      >
                        {selected.status}
                      </Badge>
                    </div>
                    <div className="text-[11px]" style={{ color: "var(--ds-color-ink-muted)" }}>
                      Submitted {formatDate(selected.created_at)}
                    </div>
                    <ContactRow
                      label="From (user)"
                      name={selected.user_display_name}
                      email={selected.user_contact_email}
                      kind="user"
                    />
                    <ContactRow
                      label="To (professional)"
                      name={pro?.name}
                      email={pro?.contact_email}
                      preferredTime={selected.preferred_contact_time}
                      kind="pro"
                    />
                    <div>
                      <div
                        className="text-[10.5px] uppercase tracking-[0.16em] font-semibold mb-1"
                        style={{ color: "var(--ds-color-ink-subtle)" }}
                      >
                        Context
                      </div>
                      <p className="text-[13px] whitespace-pre-wrap" style={{ color: "var(--ds-color-ink)" }}>
                        {selected.context}
                      </p>
                    </div>
                    {selected.status === "pending" && (
                      <AccentButton
                        data-testid="admin-consultation-forward"
                        onClick={() => forwardLead(selected.id)}
                        className="w-full justify-center"
                      >
                        Mark forwarded
                      </AccentButton>
                    )}
                    {selected.status !== "closed" && (
                      <div className="space-y-2 pt-1">
                        <Field label="Close with reason">
                          <SelectInput
                            data-testid="admin-consultation-close-reason"
                            value={closeReason}
                            onChange={(e) => setCloseReason(e.target.value)}
                            className="w-full"
                          >
                            {CLOSED_REASONS.map((r) => (
                              <option key={r.value} value={r.value}>{r.label}</option>
                            ))}
                          </SelectInput>
                        </Field>
                        <GhostButton
                          onClick={() => closeLead(selected.id, closeReason)}
                          disabled={closingId === selected.id}
                          data-testid="admin-consultation-close"
                          className="w-full justify-center"
                        >
                          {closingId === selected.id ? "Closing…" : "Close consultation"}
                        </GhostButton>
                      </div>
                    )}
                    {selected.status === "closed" && (
                      <div className="text-[11px]" style={{ color: "var(--ds-color-ink-muted)" }}>
                        Closed {formatDate(selected.closed_at)} · reason: {selected.closed_reason || "—"}
                      </div>
                    )}
                  </>
                );
              })()
            )}
          </div>
        </Section>
      </div>
    </div>
  );
}

function ContactRow({ label, name, email, preferredTime, kind }) {
  return (
    <div>
      <div
        className="text-[10.5px] uppercase tracking-[0.16em] font-semibold mb-1"
        style={{ color: "var(--ds-color-ink-subtle)" }}
      >
        {label}
      </div>
      <div className="text-[13px]" style={{ color: "var(--ds-color-ink)" }}>{name || "—"}</div>
      {email && (
        <div className="flex items-center gap-1.5">
          <span className="text-[11px]" style={{ color: "var(--ds-color-ink-muted)" }}>{email}</span>
          <button
            type="button"
            data-testid={`admin-consultation-copy-${kind}-email`}
            onClick={() => copyToClipboard(email)}
            className="text-[11px] inline-flex items-center gap-0.5 hover:underline"
            style={{ color: "var(--ds-color-accent-strong)" }}
          >
            <Copy className="w-3 h-3" /> Copy
          </button>
        </div>
      )}
      {preferredTime && (
        <div className="text-[11px] mt-1" style={{ color: "var(--ds-color-ink-muted)" }}>
          Preferred time: {preferredTime}
        </div>
      )}
    </div>
  );
}
